import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Signature, Save, Loader2, Eye } from "lucide-react";

/**
 * Per-user HTML signature. Appended to every "Send by email" outbound message
 * (below the message body, before attachments).
 */
export default function EmailSignaturePanel() {
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    api.get("/email/signature").then((r) => setHtml(r.data.html || "")).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/email/signature", { html });
      toast.success("Signature saved · it'll auto-append to your outgoing emails");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const sample = `<b>Rajesh Kumar</b><br/>
Chief Financial Officer · Zenith 360 Showcase Manufacturing Pvt Ltd<br/>
📞 +91 98765 43210 · ✉️ rajesh@zenith360.in<br/>
<a href="https://zenith360.in" style="color:#2E63FF">zenith360.in</a>`;

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="email-signature-panel">
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
            <Signature className="w-3.5 h-3.5" /> My email signature
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            HTML signature that appends to every mail you send from the "Send by email" button.
            Use plain HTML — no external images.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowPreview((s) => !s)}
                   className="rounded-sm h-8 gap-1 text-xs" data-testid="sig-toggle-preview">
            <Eye className="w-3 h-3" /> {showPreview ? "Hide" : "Show"} preview
          </Button>
          <Button variant="outline" size="sm" onClick={() => setHtml(sample)}
                   className="rounded-sm h-8 text-xs" data-testid="sig-use-sample">
            Use sample
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">HTML source</div>
          <Textarea value={html} onChange={(e) => setHtml(e.target.value)}
                      placeholder="<b>Your Name</b><br/>Designation · Company"
                      className="min-h-[220px] font-mono text-xs rounded-sm"
                      data-testid="sig-html-input" />
          <div className="text-[10px] font-mono text-muted-foreground mt-1">
            {html.length.toLocaleString()} chars · max 20 KB
          </div>
        </div>
        {showPreview && (
          <div>
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Preview</div>
            <div className="border rounded-sm p-3 min-h-[220px] bg-white">
              <div className="text-xs text-muted-foreground italic mb-2 pb-2 border-b">…your message body…</div>
              <div className="text-[13px]" style={{ color:'#666', paddingTop:'8px', borderTop:'1px solid #eee' }}
                    dangerouslySetInnerHTML={{ __html: html || "<i>(no signature saved yet)</i>" }} />
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="rounded-sm bg-accent text-white gap-1.5"
                 data-testid="sig-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save signature</>}
        </Button>
      </div>
    </div>
  );
}
